import { useContext, useEffect, useState } from 'react';
import {
    CalcButtonContainer,
    DisclaimerText,
    CalculationResultContainer,
    GridLabel,
    GridLabelSpan,
    Header,
    LargeGridItem,
    OuterCalculationResultContainer,
    ResultsContainer,
    SmallGridItem,
    Spans,
    WarningButtonContainer,
    WarningButtonText
} from './CalculationDashboard.styles';
import { getCalcProgressFromSessionStorage } from '../common/storage.functions';
import {
    CalculationResult,
    CalculationStatus
} from './CalculationResult.types';
import { calculationResultIsOKWithWarningsAndErrors } from './calculation.functions';
import { CalcProgress, DEFAULT_PROGRESS } from '../common/Progress.types';
import {
    CalculationProgressMessage,
    WEBSOCKET_ACTIONS
} from '../websocket/WebsocketWorker.types';
import EventStore from '../websocket/eventStore';
import { Checkmark } from '../../../theme/icons/Checkmark';
import { Crossmark } from '../../../theme/icons/Crossmark';
import { BLACK, Info, ORANGE, WHITE } from '@swegon-core/uic/dist/src/theme';
import { Context as TranslationContext } from '../../contexts/TranslationContext';
import { IconButton, Modal } from '@swegon-core/uic';
import CalculateButton from './CalculateButton';
import { CalculationResultChangeEvent } from './CalculationEventWrapper';
import { WarningsAndErrors } from '../../../components/WarningsAndErrors';

export type CalculationDashboardProps = {
    ahuCalculationStatus: CalculationStatus;
    eventStore: EventStore | undefined;
    calculationJobId: string | undefined;
    onCalculateButtonClick: () => void;
    onCalculationResultChanged: (event: CalculationResultChangeEvent) => void;
};

export const CalculationDashboard = ({
    eventStore,
    onCalculateButtonClick,
    ahuCalculationStatus,
    calculationJobId,
    onCalculationResultChanged
}: CalculationDashboardProps) => {
    const [calculationResult, setCalculationResult] = useState<
        CalculationResult | undefined
    >(undefined);
    const [calcErrorMessage, setCalcErrorMessage] = useState<string>();
    const hasWarnings =
        (ahuCalculationStatus &&
            [
                'CALC_RES_OK',
                'CALC_RES_OK_WITH_WARNINGS',
                'CALC_RES_OK_WITH_ERRORS'
            ].includes(ahuCalculationStatus) &&
            calculationResult?.warnings?.some(
                (warning) => warning.severityLevel !== undefined
            )) ??
        false;
    const [warningsOverlayVisible, setWarningsOverlayVisible] =
        useState(hasWarnings);
    const { l } = useContext(TranslationContext);

    const dashboardValues = calculationResult?.resultValues;
    const numberOfColumns = calculationResult?.resultValues?.length
        ? (calculationResult.resultValues.length as number)
        : 1;
    const hasCalculationResults =
        ahuCalculationStatus !== undefined &&
        calculationResultIsOKWithWarningsAndErrors(ahuCalculationStatus);

    const [calcProgress, setCalcProgress] = useState(
        calculationJobId && ahuCalculationStatus !== 'CALC_RUNNING'
            ? getCalcProgressFromSessionStorage()
            : DEFAULT_PROGRESS
    );

    const updateCalcProgress = (progress: CalcProgress) => {
        setCalcProgress(progress);
    };

    const messageToCalcProgress = (
        message: CalculationProgressMessage
    ): CalcProgress => {
        const currentStep = message.step.index;
        const totalSteps = message.lastStep;
        const percent = Math.round((currentStep / totalSteps) * 100);
        return {
            percent: percent,
            message: message.step.message
        };
    };

    useEffect(() => {
        if (!eventStore || !calculationJobId) {
            return;
        }

        const listenerCallback = (event: MessageEvent) => {
            const { task, message } = event.data;
            if (
                task === WEBSOCKET_ACTIONS.CALCULATION_PROGRESS &&
                (message as CalculationProgressMessage)?.jobId ===
                    calculationJobId
            ) {
                updateCalcProgress(messageToCalcProgress(message));
            }
        };

        const listenerName = `result_${calculationJobId}`;
        eventStore.addEventListener(listenerName, listenerCallback);
        return () => {
            eventStore.removeEventListener(listenerName);
        };
    }, [calculationJobId, eventStore]);

    const getTranslatedProgressMessage = (message: string) =>
        message && message.length
            ? l(`ahuDesign.${message}`)
            : l('ahuDesign.awaitCalculationStart');

    const onCalclateButtonClickHandler = () => {
        setCalcProgress(DEFAULT_PROGRESS);
        onCalculateButtonClick();
    };

    const onCalculationResultChangedHandler = (
        event: CalculationResultChangeEvent
    ) => {
        if (event.status === 'CALC_RES_ERROR') {
            setCalcErrorMessage(event.message);
        }
        setCalculationResult(event.result);
        onCalculationResultChanged(event);
    };

    return (
        <>
            <CalcButtonContainer>
                {hasWarnings && (
                    <WarningButtonContainer role="button">
                        <IconButton
                            color={WHITE}
                            disabled={false}
                            onClick={() => setWarningsOverlayVisible(true)}
                            noBorder={true}
                        >
                            <Info color={ORANGE} />
                            <WarningButtonText>
                                {l('ahuDesignLite.calcWarningsAndErrorsText')}
                            </WarningButtonText>
                        </IconButton>
                    </WarningButtonContainer>
                )}
                <CalculateButton
                    ahuCalculationStatus={ahuCalculationStatus}
                    eventStore={eventStore}
                    handleCalculationClick={onCalclateButtonClickHandler}
                    onCalculationResultChanged={
                        onCalculationResultChangedHandler
                    }
                />
            </CalcButtonContainer>
            <OuterCalculationResultContainer $numberofcolumns={numberOfColumns}>
                {hasCalculationResults && (
                    <Header>
                        {dashboardValues
                            ?.filter(
                                (item) =>
                                    item.value !== undefined &&
                                    item.value !== null
                            )
                            .map((item) => (
                                <Spans key={item.title}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: item.title
                                        }}
                                    ></span>
                                </Spans>
                            ))}
                    </Header>
                )}
                <CalculationResultContainer $numberofcolumns={numberOfColumns}>
                    {hasCalculationResults && (
                        <ResultsContainer>
                            {dashboardValues &&
                                dashboardValues
                                    ?.filter(
                                        (item) =>
                                            item.value !== undefined &&
                                            item.value !== null
                                    )
                                    .map((item) => {
                                        const shouldHighlightWarnings =
                                            item?.severityLevel !== undefined &&
                                            item?.severityLevel > 1;

                                        return (
                                            <SmallGridItem
                                                key={`small-grid-item-${item.sortNo}`}
                                                $shouldhighlightwarnings={`${shouldHighlightWarnings}`}
                                            >
                                                <GridLabel
                                                    id={'id-' + item.sortNo}
                                                >
                                                    <GridLabelSpan>
                                                        {typeof item.value ===
                                                        'boolean' ? (
                                                            item.value ? (
                                                                <Checkmark
                                                                    color={
                                                                        BLACK
                                                                    }
                                                                    width={20}
                                                                    height={20}
                                                                />
                                                            ) : (
                                                                <Crossmark
                                                                    color={
                                                                        BLACK
                                                                    }
                                                                    width={20}
                                                                    height={20}
                                                                />
                                                            )
                                                        ) : (
                                                            item.value
                                                        )}
                                                    </GridLabelSpan>
                                                </GridLabel>
                                            </SmallGridItem>
                                        );
                                    })}
                        </ResultsContainer>
                    )}
                    {ahuCalculationStatus === 'CALC_RUNNING' && (
                        <LargeGridItem
                            $calculationstatus={ahuCalculationStatus}
                            $calccompletion={calcProgress?.percent ?? 0}
                        >
                            <GridLabel
                                $calculationstatus={ahuCalculationStatus}
                            >
                                {getTranslatedProgressMessage(
                                    calcProgress?.message ?? ''
                                )}
                            </GridLabel>
                        </LargeGridItem>
                    )}
                    {ahuCalculationStatus === 'CALC_RES_ERROR' && (
                        <LargeGridItem
                            $calculationstatus={ahuCalculationStatus}
                            $calccompletion={100}
                            $calcerrormessage={calcErrorMessage}
                        >
                            <GridLabel
                                id={'id-title'}
                                $calculationstatus={ahuCalculationStatus}
                            >
                                {calcErrorMessage}
                            </GridLabel>
                        </LargeGridItem>
                    )}
                    {(ahuCalculationStatus === 'CALC_NEEDS_REFRESH' ||
                        ahuCalculationStatus === 'CALC_DISABLED') && (
                        <LargeGridItem
                            $calculationstatus={ahuCalculationStatus}
                            $calccompletion={100}
                        >
                            <GridLabel id={'id-title'}>
                                {l('ahuDesign.calcResultMissing')}
                            </GridLabel>
                        </LargeGridItem>
                    )}
                </CalculationResultContainer>
            </OuterCalculationResultContainer>
            <DisclaimerText>
                {hasCalculationResults
                    ? l('ahuDesignLite.calcDisclaimer')
                    : l('ahuDesignLite.generalDisclaimer')}
            </DisclaimerText>
            <Modal
                open={warningsOverlayVisible}
                onClose={() => setWarningsOverlayVisible(false)}
                title={l('CalculationErrorPopup.heading')}
            >
                <WarningsAndErrors
                    key={0}
                    warnings={calculationResult?.warnings ?? []}
                />
            </Modal>
        </>
    );
};
